'use client';

// import { ExportPdfDialog } from '@/components/research/export-pdf'; // Hidden: PDF export feature temporarily disabled
import { DeleteSessionDialog } from '@/components/research/DeleteSessionDialog';
import { ShareSessionModal } from '@/components/research/ShareSessionModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SharePopover } from '@/components/ui/share-popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useAuthUser } from '@/hooks/useAuthUser';
import { Link, useRouter } from '@/i18n/routing';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { selectGeneratingTitleForSessionId } from '@/lib/redux/selectors/historySelectors';
import {
  fetchSessionMessages,
  removeSessionById,
  setActiveSession,
  updateSessionTitleOnly,
} from '@/lib/redux/slices/history';
import { cn } from '@/lib/utils';
import type { ApiResp } from '@/services/api-services';
import storageService from '@/services/storage-service';
import type { TitleResponse } from '@/services/title-services';
import { updateTitle } from '@/services/title-services';
import usersServices from '@/services/users-services';
import {
  Building2,
  Edit3,
  // FileDown, // Hidden: PDF export feature temporarily disabled
  Loader2,
  MoreHorizontal,
  ShareIcon,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

// Updated Session interface to include necessary fields
interface Session {
  sess_id: number;
  session_id: string;
  title: string;
  timestamp: number;
  isActive?: boolean; // Make isActive optional if needed for styling
  messagesFetched: boolean;
  access_role?: 'owner' | 'shared_direct' | 'shared_group';
  org_id?: string | null;
}

/** Route a session opens into. Chat is the default; search sessions open the
 * search session view. (Document-analysis has no dedicated session route yet,
 * so those fall back to chat — which renders any session type.) */
export type SessionPathname =
  | '/chat/[sessionId]'
  | '/search/[sessionId]'
  | '/document-analysis/[sessionId]'
  | '/generovanie/[sessionId]';

interface SessionItemProps {
  session: Session;
  /** Show org/personal badge only when the session list contains a mix of both types. */
  showContextBadge?: boolean;
  onSessionClick?: (() => void) | undefined;
  /** Destination route template for this session. Defaults to the chat view. */
  sessionPathname?: SessionPathname | undefined;
}

const TYPING_SPEED_MS = 50; // Milliseconds per character

export const SessionItem = ({
  session,
  showContextBadge,
  onSessionClick,
  sessionPathname = '/chat/[sessionId]',
}: SessionItemProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const params = useParams();
  const currentRouteSessionId = params['sessionId'] as string;
  const { toast } = useToast();
  const { user } = useAuth();
  const { isInOrg, orgName } = useAuthUser();
  const t = useTranslations('SessionItem');

  const generatingTitleForSessionId = useAppSelector(
    selectGeneratingTitleForSessionId
  );
  const isLoadingTitle = generatingTitleForSessionId === session.session_id;

  const [isEditing, setIsEditing] = useState(false);
  // const [showExportDialog, setShowExportDialog] = useState(false); // Hidden: PDF export feature temporarily disabled
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editableTitle, setEditableTitle] = useState(session.title);
  const [displayTitle, setDisplayTitle] = useState(
    isLoadingTitle ? '' : session.title
  ); // Initialize displayTitle based on loading state

  const animationFrameIdRef = useRef<number | null>(null);
  const previousTitleRef = useRef<string>(session.title); // Track previous title value
  const wasLoadingTitleRef = useRef<boolean>(false); // Track if this session was generating a title
  const wasEditingRef = useRef<boolean>(false); // Track if user was editing

  // Sync editableTitle with session.title prop
  useEffect(() => {
    setEditableTitle(session.title);
  }, [session.title]);

  // Update page title when this session is active
  useEffect(() => {
    if (session.session_id === currentRouteSessionId && session.title) {
      document.title = session.title;
    }
  }, [session.session_id, currentRouteSessionId, session.title]);

  // Track loading state transitions - set flag when loading starts
  useEffect(() => {
    if (isLoadingTitle) {
      wasLoadingTitleRef.current = true;
    }
  }, [isLoadingTitle]);

  // Track editing state transitions - set flag when editing starts
  useEffect(() => {
    if (isEditing) {
      wasEditingRef.current = true;
    }
  }, [isEditing]);

  // Typing animation effect for displayTitle
  useEffect(() => {
    // Always cancel previous animation if dependencies change
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    if (isEditing) {
      // If editing, animation should not run.
      // displayTitle will naturally be whatever it was or will be updated if editing is cancelled/submitted.
      return;
    }

    if (isLoadingTitle) {
      setDisplayTitle(''); // Show empty while loading title
      return;
    }

    const targetTitle = session.title;
    const previousTitle = previousTitleRef.current;

    // Check if the title value actually changed (not just the reference)
    const titleValueChanged = targetTitle !== previousTitle;

    // If the title value didn't change, just sync displayTitle without animation
    if (!titleValueChanged && displayTitle !== targetTitle) {
      setDisplayTitle(targetTitle);
      return;
    }

    // If displayTitle is already correct and targetTitle is not empty, no animation needed.
    if (displayTitle === targetTitle && targetTitle !== '') {
      previousTitleRef.current = targetTitle; // Update ref
      return;
    }

    // If the targetTitle is empty, just set displayTitle to empty and stop.
    if (targetTitle === '') {
      setDisplayTitle('');
      previousTitleRef.current = targetTitle; // Update ref
      return;
    }

    // At this point, an animation might be needed because:
    // - Not editing, not loading title.
    // - targetTitle is not empty.
    // - The title value actually changed (not just a prop reference change).
    // - displayTitle is not currently matching targetTitle.

    // Only animate if:
    // 1. The title value actually changed AND
    // 2. This session was generating a title OR was being edited
    const shouldAnimate =
      titleValueChanged &&
      (wasLoadingTitleRef.current || wasEditingRef.current);

    if (!shouldAnimate) {
      setDisplayTitle(targetTitle);
      previousTitleRef.current = targetTitle; // Update ref
      return;
    }

    // Reset the flags after deciding to animate
    wasLoadingTitleRef.current = false;
    wasEditingRef.current = false;

    setDisplayTitle(''); // Reset to empty to start typing from scratch

    let charIndex = 0;
    let lastFrameTime = 0;

    const typeCharacter = (timestamp: number) => {
      if (!lastFrameTime) {
        lastFrameTime = timestamp;
      }
      const deltaTime = timestamp - lastFrameTime;

      if (deltaTime > TYPING_SPEED_MS) {
        lastFrameTime = timestamp;

        // Use functional update to build the string character by character
        setDisplayTitle(() => {
          // Ensure we don't try to substring beyond targetTitle's length
          // charIndex here represents the count of characters to display
          if (charIndex < targetTitle.length) {
            return targetTitle.substring(0, charIndex + 1);
          }
          // If charIndex has reached targetTitle.length, the full title is displayed
          animationFrameIdRef.current = null; // Stop animation
          return targetTitle; // Ensure final title is exactly targetTitle
        });

        charIndex++;

        // If the animation is not stopped (i.e., full title not yet displayed)
        if (animationFrameIdRef.current !== null) {
          animationFrameIdRef.current = requestAnimationFrame(typeCharacter);
        }
      } else {
        // If not enough time has passed, request another frame
        animationFrameIdRef.current = requestAnimationFrame(typeCharacter);
      }
    };

    animationFrameIdRef.current = requestAnimationFrame(typeCharacter);

    // Update the previous title ref to the current title value
    // This happens after we've confirmed the animation should run
    previousTitleRef.current = targetTitle;

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.title, isEditing, isLoadingTitle, dispatch]); // Added dispatch to deps as good practice, though not directly used in logic affecting animation frame.
  // displayTitle is intentionally omitted from deps to prevent loops, as it's controlled by the animation.

  const handleItemClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isEditing || isLoadingTitle) {
      e.preventDefault();
      return;
    }

    // Block navigation when any modal is open — React portal events bubble through
    // the React tree so clicks inside dialogs can reach this Link handler.
    if (showShareModal || showDeleteDialog) {
      e.preventDefault();
      return;
    }

    // Block navigation if the click originated from an interactive element
    // (button, input, or Radix menu item) so dropdowns/toggles don't navigate.
    const target = e.target as HTMLElement;
    if (
      target.closest('button, input, [role="menuitem"], [role="switch"], label')
    ) {
      e.preventDefault();
      return;
    }

    // Only dispatch actions on left-click without modifier keys
    // This allows Ctrl+Click, Cmd+Click, and middle-click to open in new tab
    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      dispatch(setActiveSession(session.session_id));
      if (!session.messagesFetched) {
        dispatch(fetchSessionMessages(session.session_id));
      }
      // Collapse sidebar on touch devices when clicking a session
      onSessionClick?.();
    }
  };

  const handleDoubleClick = () => {
    if (isLoadingTitle) return;
    setEditableTitle(session.title);
    setIsEditing(true);
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditableTitle(event.target.value);
  };

  const handleTitleSubmit = async () => {
    setIsEditing(false);
    const trimmedTitle = editableTitle.trim();

    if (trimmedTitle === '' || trimmedTitle === session.title) {
      setEditableTitle(session.title);
      // displayTitle will be updated by the useEffect watching session.title
      return;
    }

    try {
      const response: ApiResp<TitleResponse> = await updateTitle({
        session_id: session.session_id,
        title: trimmedTitle,
      });

      if (response && response.data && response.status === 'success') {
        dispatch(
          updateSessionTitleOnly({
            sessionId: session.session_id,
            title: response.data.title,
          })
        );
        // setEditableTitle(response.data.title); // Let the useEffect handle this via session.title prop change
      } else {
        toast({
          title: t('errorUpdatingTitle'),
          description: response?.message || t('couldNotUpdateSessionTitle'),
          variant: 'destructive',
        });
        setEditableTitle(session.title);
      }
    } catch {
      toast({
        title: t('errorUpdatingTitle'),
        description: t('unexpectedError'),
        variant: 'destructive',
      });
      setEditableTitle(session.title);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleTitleSubmit();
    } else if (event.key === 'Escape') {
      setIsEditing(false);
      setEditableTitle(session.title);
    }
  };

  const handleBlur = () => {
    handleTitleSubmit();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async (deleteFiles: boolean) => {
    if (!user || !user.id) {
      toast({
        title: t('error'),
        description: t('userNotFoundCannotDelete'),
        variant: 'destructive',
      });
      return;
    }

    try {
      // Delete files first if requested
      if (deleteFiles) {
        const filesResponse = await storageService.deleteSessionFiles(
          session.session_id,
          true
        );
        if (filesResponse.status !== 'success') {
          console.warn(
            'Failed to delete session files:',
            filesResponse.message
          );
          // Continue with session deletion even if file deletion fails
        }
      }

      // Delete the session
      const response = await usersServices.deleteUserHistory(
        'chat',
        session.sess_id.toString()
      );

      if (response.status === 'success') {
        dispatch(removeSessionById(session.session_id));
        toast({
          title: t('sessionDeleted'),
          description: t('sessionSuccessfullyDeleted'),
          variant: 'success',
        });
        setShowDeleteDialog(false);
        if (currentRouteSessionId === session.session_id) {
          router.push('/chat');
        }
      } else {
        toast({
          title: t('errorDeletingSession'),
          description: response.message || t('couldNotDeleteSession'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: t('errorDeletingSession'),
        description: String(error) || t('unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <li key={session.session_id} className='my-0'>
      <Link
        href={
          sessionPathname === '/search/[sessionId]'
            ? {
                pathname: '/search/[sessionId]',
                params: { sessionId: session.session_id },
              }
            : sessionPathname === '/document-analysis/[sessionId]'
              ? {
                  pathname: '/document-analysis/[sessionId]',
                  params: { sessionId: session.session_id },
                }
              : sessionPathname === '/generovanie/[sessionId]'
                ? {
                    pathname: '/generovanie/[sessionId]',
                    params: { sessionId: session.session_id },
                  }
                : {
                    pathname: '/chat/[sessionId]',
                    params: { sessionId: session.session_id },
                  }
        }
        className={cn(
          'group px-1.5 py-2 min-h-11 rounded-md hover:bg-interactive-hover cursor-pointer transition-colors text-sm flex items-center justify-between',
          session.session_id === currentRouteSessionId &&
            !isEditing &&
            'bg-interactive-selected'
        )}
        onClick={handleItemClick}
        onDoubleClick={handleDoubleClick}
      >
        {isEditing ? (
          <input
            type='text'
            value={editableTitle} // Bind to editableTitle
            onChange={handleTitleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus
            className='font-medium w-full bg-transparent border-0 focus:outline-none focus:border-b-2 focus:border-brand-primary-color p-0 text-sm'
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          // Display a placeholder if loading, otherwise the animated displayTitle
          <div className='font-medium truncate flex-grow mr-2 flex items-center gap-1.5'>
            <span className='truncate'>
              {isLoadingTitle ? '' : displayTitle}
            </span>
            {/* Org/Personal icon — only shown when session list has a mix */}
            {showContextBadge && !isLoadingTitle && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='shrink-0 text-content-muted'>
                      {session.org_id ? (
                        <Building2 className='h-3 w-3' />
                      ) : (
                        <User className='h-3 w-3' />
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side='right' className='text-xs'>
                    {session.org_id ? (orgName ?? t('org')) : t('personal')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
        {/* Conditional rendering for Loader or More Options Button */}
        {isLoadingTitle && !isEditing && (
          <Loader2 className='animate-spin w-4 h-4 text-content-muted flex-shrink-0' />
        )}
        {!isLoadingTitle && !isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className='opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-1 rounded-full hover:bg-interactive-hover focus:outline-none focus:ring-2 focus:ring-line-default flex-shrink-0 text-sm'
                onClick={(e) => {
                  e.stopPropagation(); // Prevent li's onClick, allow dropdown to handle its own state
                }}
                aria-label={t('moreOptions')}
              >
                <MoreHorizontal className='w-4 h-4 text-content-muted group-hover:text-content-secondary' />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              onClick={(e) => e.stopPropagation()} // Prevent li's onClick when clicking inside the menu
              className='w-40 p-1 text-sm'
              align='end' // Align to the right of the trigger button
              sideOffset={4} // Add a small offset from the trigger button
            >
              <DropdownMenuLabel className='text-xs text-muted-foreground'>
                {t('moreOptions')}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className='cursor-pointer text-sm px-2 py-1.5 hover:bg-accent'
              >
                <SharePopover sessionId={session.session_id}>
                  <button className='flex items-center w-full text-sm'>
                    <ShareIcon className='mr-2 h-4 w-4' />
                    <span>{t('share')}</span>
                  </button>
                </SharePopover>
              </DropdownMenuItem>

              {isInOrg &&
                (session.access_role === 'owner' ||
                  session.access_role === undefined) && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowShareModal(true);
                    }}
                    className='cursor-pointer text-sm px-2 py-1.5 hover:bg-accent'
                  >
                    <Users className='mr-2 h-4 w-4' />
                    <span>{t('shareWithTeam')}</span>
                  </DropdownMenuItem>
                )}

              {/* Hidden: PDF export feature temporarily disabled
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExportDialog(true);
                }}
                className='cursor-pointer text-sm px-2 py-1.5 hover:bg-accent'
              >
                <FileDown className='mr-2 h-4 w-4' />
                <span>{t('exportToPdf')}</span>
              </DropdownMenuItem>
              */}

              {(session.access_role === 'owner' ||
                session.access_role === undefined) && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDoubleClick(); // Reuse existing rename logic
                  }}
                  className='cursor-pointer text-sm px-2 py-1.5 hover:bg-accent'
                >
                  <Edit3 className='mr-2 h-4 w-4' />
                  <span>{t('rename')}</span>
                  {/* <DropdownMenuShortcut>F2</DropdownMenuShortcut> */}
                </DropdownMenuItem>
              )}
              {(session.access_role === 'owner' ||
                session.access_role === undefined) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleDeleteClick}
                    className='text-status-error hover:!text-status-error focus:!text-status-error hover:!bg-status-error/10 focus:!bg-status-error/10 cursor-pointer text-sm px-2 py-1.5'
                  >
                    <Trash2 className='mr-2 h-4 w-4 text-status-error' />
                    <span>{t('delete')}</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Hidden: PDF export feature temporarily disabled
        <ExportPdfDialog
          sessionId={session.session_id}
          isOpen={showExportDialog}
          onClose={() => setShowExportDialog(false)}
        />
        */}
      </Link>
      {/* Dialogs are outside <Link> to prevent Radix focus management from
          triggering Link navigation when the modals open/close. */}
      <DeleteSessionDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        sessionId={session.session_id}
        sessionTitle={session.title}
        onConfirm={handleDeleteConfirm}
      />
      <ShareSessionModal
        sessionId={session.session_id}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </li>
  );
};
